import { useState } from "react";
import toast from "../../utils/toast.js";
import { searchRoutes } from "../../services/carsService.js";
import BookingModal from "../components/BookingModal.jsx";

const CITIES = ["Lucknow", "Kanpur", "Hardoi", "Sitapur", "Unnao"];

export default function BookRide() {
  const [currentTab, setCurrentTab] = useState("per-seat");
  const [fromCity, setFromCity] = useState("");
  const [toCity, setToCity] = useState("");
  const [travelDate, setTravelDate] = useState("");
  const [passengers, setPassengers] = useState("1");

  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [results, setResults] = useState([]);

  const [bookingRoute, setBookingRoute] = useState(null);

  const today = new Date().toISOString().split("T")[0];

  function swapCities() {
    setFromCity(toCity);
    setToCity(fromCity);
  }

  async function runSearch(bookingType) {
    if (!fromCity || !toCity) {
      toast.error("Please select both cities.");
      return;
    }
    if (fromCity === toCity) {
      toast.error("From and To cities can't be the same.");
      return;
    }
    if (!travelDate) {
      toast.error("Please pick a travel date.");
      return;
    }

    setSearching(true);
    try {
      const res = await searchRoutes({ fromCity, toCity, bookingType, travelDate });
      if (!res.success) {
        toast.error(res.message || "Couldn't search cabs.");
        return;
      }
      setResults(res.data || []);
      setHasSearched(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't search cabs.");
    } finally {
      setSearching(false);
    }
  }

  function handleSearch(e) {
    e.preventDefault();
    runSearch(currentTab);
  }

  function switchTab(tab) {
    setCurrentTab(tab);
    if (hasSearched) runSearch(tab);
  }

  return (
    <>
      <section className="dash-section active" data-section="book">
        <div className="section-head-row">
          <h2>Book a ride</h2>
        </div>
        <div className="search-card dash-search-card">
          <div className="search-tabs">
            <button
              type="button"
              className={`search-tab ${currentTab === "per-seat" ? "active" : ""}`}
              onClick={() => switchTab("per-seat")}
            >
              🧑‍🤝‍🧑 Per-Seat Sharing
            </button>
            <button
              type="button"
              className={`search-tab ${currentTab === "full-cab" ? "active" : ""}`}
              onClick={() => switchTab("full-cab")}
            >
              🚙 Full Cab
            </button>
          </div>
          <form onSubmit={handleSearch}>
            <div className="field-row">
              <div className="field">
                <label htmlFor="fromCity">From</label>
                <select
                  id="fromCity"
                  value={fromCity}
                  onChange={(e) => setFromCity(e.target.value)}
                >
                  <option value="">Select city</option>
                  {CITIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="swap-btn"
                id="swapBtn"
                aria-label="Swap cities"
                onClick={swapCities}
              >
                ⇅
              </button>
              <div className="field">
                <label htmlFor="toCity">To</label>
                <select
                  id="toCity"
                  value={toCity}
                  onChange={(e) => setToCity(e.target.value)}
                >
                  <option value="">Select city</option>
                  {CITIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field-row-2">
              <div className="field">
                <label htmlFor="travelDate">Travel date</label>
                <input
                  type="date"
                  id="travelDate"
                  min={today}
                  value={travelDate}
                  onChange={(e) => setTravelDate(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="passengers">Passengers</label>
                <select
                  id="passengers"
                  value={passengers}
                  onChange={(e) => setPassengers(e.target.value)}
                >
                  <option>1</option>
                  <option>2</option>
                  <option>3</option>
                  <option>4</option>
                  <option>5</option>
                  <option>6</option>
                </select>
              </div>
            </div>
            <button type="submit" className="btn btn-primary btn-banana-2" disabled={searching}>
              {searching ? "Searching..." : "Search Cabs →"}
            </button>
          </form>
        </div>

        <div className="section-head-row" style={{ marginTop: 28 }}>
          <h2 id="resultsHeading">
            {hasSearched ? `Available cabs — ${fromCity} → ${toCity}` : "Available cabs"}
          </h2>
        </div>

        {hasSearched && results.length > 0 && (
          <div className="cab-results" id="cabResults">
            {results.map((route) => {
              const car = route.car || {};
              const prices = [route.rateCard?.front, route.rateCard?.middle, route.rateCard?.rear].filter(
                (p) => p != null && p !== 0,
              );
              const min = prices.length ? Math.min(...prices) : 0;
              const max = prices.length ? Math.max(...prices) : 0;

              return (
                <article className="cab-card" key={route._id}>
                  <div className="cab-card-top">
                    <div>
                      <h3 className="cab-card-model">{car.carModel}</h3>
                      <span className="cab-card-driver">👤 {car.driverName}</span>
                    </div>
                    <span className="badge-live">Verified</span>
                  </div>
                  <div className="cab-card-meta">
                    <span className="meta-fuel">⛽ {car.fuelType}</span>
                    <span className="meta-seats">💺 {route.seatsLeft ?? 0} seats left</span>
                    <span className="meta-departures">
                      🕐 {route.dailyDepartures?.length || 0}/day
                    </span>
                  </div>
                  <div className="cab-card-bottom">
                    <div className="cab-card-price">
                      {currentTab === "full-cab" ? (
                        <>
                          <strong>₹{route.rateCard?.fullCab || 0}</strong>
                          <small> / full cab</small>
                        </>
                      ) : (
                        <>
                          <strong>
                            ₹{min}
                            {max !== min ? `–${max}` : ""}
                          </strong>
                          <small> / seat</small>
                        </>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm cab-book-btn"
                      onClick={() => setBookingRoute(route)}
                      disabled={currentTab === "per-seat" && (route.seatsLeft ?? 0) <= 0}
                    >
                      {currentTab === "per-seat" && (route.seatsLeft ?? 0) <= 0
                        ? "Sold Out"
                        : "Book Now"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {hasSearched && results.length === 0 && (
          <div className="empty-state show" id="emptyStateResults">
            <h3>No cabs found</h3>
            <p>
              No {currentTab === "full-cab" ? "full cab" : "per-seat"} rides found for{" "}
              {fromCity} → {toCity}. Try a different route.
            </p>
          </div>
        )}

        {!hasSearched && (
          <div className="empty-state show" id="emptyStateResults">
            <h3>Search for a route</h3>
            <p>Pick a from &amp; to city above and hit "Search Cabs" to see available rides.</p>
          </div>
        )}
      </section>

      <BookingModal
        isOpen={!!bookingRoute}
        route={bookingRoute}
        bookingType={currentTab}
        travelDate={travelDate}
        onClose={() => setBookingRoute(null)}
      />
    </>
  );
}
